import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useT, useLang } from "@/i18n";
import { Mail, Lock, Languages, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import logo from "@/assets/logo.png";
import { FlagRO, FlagUS } from "@/components/CountryFlag";
import { LegalFooter } from "@/components/LegalFooter";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const t = useT();
  const lang = useLang((s) => s.lang);
  const setLang = useLang((s) => s.setLang);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      const pending = (() => {
        try {
          return localStorage.getItem("pendingLeagueJoin");
        } catch {
          return null;
        }
      })();
      if (pending) {
        navigate({ to: "/join/$leagueCode", params: { leagueCode: pending } });
      } else {
        navigate({ to: "/predictions" });
      }
    }
  }, [loading, user, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setNeedsVerification(false);
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    setSubmitting(false);
    if (error) {
      const code = (error as { code?: string }).code;
      const msg = error.message.toLowerCase();
      if (code === "email_not_confirmed" || msg.includes("not confirmed") || msg.includes("not verified")) {
        setNeedsVerification(true);
        toast.error(t("Please verify your email first. Check your inbox.", "Te rugăm să-ți verifici emailul mai întâi. Verifică inbox-ul."));
      } else {
        toast.error(error.message);
      }
      return;
    }
    // Redirect handled by useEffect once auth state updates (honors pending_league_join).
  }

  async function handleResendVerification() {
    if (!email.trim()) {
      toast.error(t("Enter your email first", "Introduceți emailul mai întâi"));
      return;
    }
    setResending(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: email.trim(),
      options: { emailRedirectTo: `${window.location.origin}/login` },
    });
    setResending(false);
    if (error) toast.error(error.message);
    else toast.success(t("Verification email sent", "Email de verificare trimis"));
  }

  async function handleReset() {
    if (!email.trim()) {
      toast.error(t("Enter your email first", "Introduceți emailul mai întâi"));
      return;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) toast.error(error.message);
    else toast.success(t("Reset email sent", "Email-ul de resetare a fost trimis"));
  }


  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-accent">
      <div className="mx-auto flex min-h-screen max-w-md flex-col px-4 pt-2 pb-6">
        <div className="mb-2 text-center">
          <img src={logo} alt="Predictor Pro" className="mx-auto h-48 w-48 object-contain drop-shadow-2xl" />
          <h1 className="-mt-2 text-3xl font-bold tracking-tight">Predictor Pro</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("Sign in to continue.", "Autentifică-te pentru a continua.")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="rounded-2xl border bg-card p-6 shadow-sm">
          <label className="block">
            <span className="flex items-center gap-1.5 text-sm font-medium"><Mail className="h-4 w-4" />{t("Email", "Email")}</span>
            <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-base" />
          </label>
          <label className="mt-4 block">
            <span className="flex items-center gap-1.5 text-sm font-medium"><Lock className="h-4 w-4" />{t("Password", "Parolă")}</span>
            <div className="relative mt-1">
              <input type={showPassword ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full rounded-lg border border-input bg-background px-3 py-2.5 pr-11 text-base" />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-muted-foreground transition-colors hover:text-foreground"
                aria-label={showPassword ? t("Hide password", "Ascunde parola") : t("Show password", "Arată parola")}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </label>

          {needsVerification && (
            <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
              <p className="font-semibold">
                {t("Please verify your email first.", "Te rugăm să-ți verifici emailul mai întâi.")}
              </p>
              <p className="mt-1 text-muted-foreground">
                {t("Check your inbox for the verification link.", "Verifică inbox-ul pentru link-ul de verificare.")}
              </p>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resending}
                className="mt-2 text-sm font-semibold text-primary hover:underline disabled:opacity-50"
              >
                {resending ? t("Sending…", "Se trimite…") : t("Resend verification email", "Retrimite emailul de verificare")}
              </button>
            </div>
          )}

          <button type="submit" disabled={submitting} className="mt-6 w-full rounded-lg bg-primary py-3 text-base font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50">
            {submitting ? t("Signing in…", "Se autentifică…") : t("Sign in", "Autentificare")}
          </button>


          <div className="mt-3 flex items-center justify-between text-sm">
            <button type="button" onClick={handleReset} className="text-muted-foreground hover:text-primary hover:underline">
              {t("Forgot password?", "Ai uitat parola?")}
            </button>
            <Link to="/signup" className="font-semibold text-primary hover:underline">
              {t("Create account", "Creează cont")}
            </Link>
          </div>

          <div className="mt-6 border-t border-border/60 pt-4">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Languages className="h-3.5 w-3.5" />
              {t("Language", "Limbă")}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {([
                { code: "en", label: "English", Flag: FlagUS },
                { code: "ro", label: "Română", Flag: FlagRO },
              ] as const).map((opt) => (
                <button
                  key={opt.code}
                  type="button"
                  onClick={() => setLang(opt.code)}
                  className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                    lang === opt.code
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  }`}
                >
                  <opt.Flag className="h-4 w-6 rounded-sm shadow-sm" />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <LegalFooter className="mt-6" />
        </form>
      </div>
    </div>
  );
}
